import { localizeDateNames } from "@/lib/dateNames";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
/**
 * LegalTab — Super Admin · Legal Documents module.
 *
 * Permanent home for legal document management. Replaces the previous
 * one-time seed script (`scripts/legal_seed_documents.py`) — Super
 * Admins bring a fresh production database into the desired state
 * with a single click of "Initialize Legal Documents" instead of ever
 * touching a shell.
 *
 * Two sub-tabs:
 *   • Documents            — full history + create/edit/publish
 *   • Company Legal Status — cross-company snapshot of acceptance
 *
 * Acceptance remains COMPANY scoped (one row per company/document
 * type). This tab never edits `legal_acceptances`; it only manages
 * document versions and shows read-only acceptance state.
 */
import i18n from "@/i18n";
import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import {
    Plus, Edit2, Sparkles, ShieldCheck, Building2, Loader2,
} from "lucide-react";

const DOC_TYPES = ["msa", "privacy", "dpa", "sla", "cookie", "tos"];

const TYPE_LABEL = {
    get msa() { return i18n.t("admin.doc.msa", "Master Service Agreement"); },
    get privacy() { return i18n.t("admin.doc.privacy", "Privacy Policy"); },
    get dpa() { return i18n.t("admin.doc.dpa", "Data Processing Agreement"); },
    get sla() { return i18n.t("admin.doc.sla", "Service Level Agreement"); },
    get cookie() { return i18n.t("admin.doc.cookie", "Cookie Policy"); },
    get tos() { return i18n.t("admin.doc.tos", "Terms of Service"); },
};


function formatDate(iso) {
    if (!iso) return "—";
    try {
        return localizeDateNames(new Date(iso).toLocaleString(undefined,
            { year: "numeric", month: "short", day: "2-digit",
              hour: "2-digit", minute: "2-digit" }));
    } catch { return iso; }
}


export default function LegalTab() {
    const [subtab, setSubtab] = useState("documents");
    return (
        <div className="space-y-6" data-testid="admin-legal-tab">
            <div className="flex items-center gap-2 border-b border-neutral-200">
                <button
                    data-testid="legal-subtab-documents"
                    onClick={() => setSubtab("documents")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${subtab === "documents"
                        ? "border-neutral-900 text-neutral-900"
                        : "border-transparent text-neutral-500 hover:text-neutral-900"}`}
                >
                    <ShieldCheck className="inline w-4 h-4 mr-1.5 -mt-0.5" />
                    Documents
                </button>
                <button
                    data-testid="legal-subtab-company-status"
                    onClick={() => setSubtab("company-status")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${subtab === "company-status"
                        ? "border-neutral-900 text-neutral-900"
                        : "border-transparent text-neutral-500 hover:text-neutral-900"}`}
                >
                    <Building2 className="inline w-4 h-4 mr-1.5 -mt-0.5" />
                    {i18n.t("admin.l.Company_Legal_Status", "Company Legal Status")}
                </button>
            </div>

            {subtab === "documents" && <DocumentsPanel />}
            {subtab === "company-status" && <CompanyStatusPanel />}
        </div>
    );
}


function DocumentsPanel() {
    const [docs, setDocs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(false);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/legal/documents");
            setDocs(data.documents || []);
        } catch (err) {
            toast.error((i18n.language === "fi" ? apiErrorMessage(err) : formatApiErrorDetail(err)));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    async function handleInitialize() {
        if (initializing) return;
        setInitializing(true);
        try {
            const { data } = await api.post("/admin/legal/initialize");
            const lines = (data.results || []).map((r) => {
                const label = TYPE_LABEL[r.document_type] || r.document_type;
                const verb = r.action === "created" ? i18n.t("admin.action.created", "created")
                           : r.action === "synced" ? i18n.t("admin.action.synced", "synced")
                           : i18n.t("admin.action.upToDate", "already up to date");
                return `${label} ${verb}`;
            });
            toast.success(lines.join(" · ") + i18n.t("admin.initCompleted", " · Initialization completed."),
                { duration: 6000 });
            await reload();
        } catch (err) {
            toast.error((i18n.language === "fi" ? apiErrorMessage(err) : formatApiErrorDetail(err)));
        } finally {
            setInitializing(false);
        }
    }

    if (loading && docs === null) {
        return (
            <div className="py-10 text-center text-neutral-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
        );
    }
    if (docs === null) return null;

    const isEmpty = docs.length === 0;

    return (
        <div className="space-y-5">
            {isEmpty && (
                <div className="rounded-xl border border-dashed border-amber-300
                                bg-amber-50 px-6 py-8 text-center"
                     data-testid="legal-init-empty">
                    <Sparkles className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                    <h3 className="text-lg font-semibold text-neutral-900">
                        {i18n.t("admin.l.No_legal_documents_yet", "No legal documents yet")}
                    </h3>
                    <p className="mt-1 text-sm text-neutral-600 max-w-md mx-auto">
                        {i18n.t("admin.bootstrapHint", "Bootstrap the six official MiseOne legal documents (MSA, Privacy, DPA, SLA, Cookie Policy, Terms of Service) in one click. Safe to run at any time — subsequent clicks are a no-op.")}
                    </p>
                    <Button
                        onClick={handleInitialize}
                        disabled={initializing}
                        className="mt-4"
                        data-testid="legal-initialize-btn"
                    >
                        {initializing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {i18n.t("admin.l.Initialize_Legal_Documents", "Initialize Legal Documents")}
                    </Button>
                </div>
            )}

            {!isEmpty && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-neutral-500">
                        {docs.length} document{docs.length === 1 ? "" : "s"} on file
                    </div>
                    <Button size="sm" onClick={() => setCreating(true)}
                            data-testid="legal-new-doc-btn">
                        <Plus className="w-4 h-4 mr-1.5" />
                        {i18n.t("admin.l.New_Document", "New Document")}
                    </Button>
                </div>
            )}

            {!isEmpty && (
                <div className="overflow-x-auto border border-neutral-200 rounded-xl"
                     data-testid="legal-docs-table">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-left text-xs
                                          uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="px-4 py-3">{i18n.t("admin.l.Document_Type", "Document Type")}</th>
                                <th className="px-4 py-3">{i18n.t("admin.l.Version", "Version")}</th>
                                <th className="px-4 py-3">{i18n.t("admin.l.Active", "Active")}</th>
                                <th className="px-4 py-3">{i18n.t("admin.l.Required", "Required")}</th>
                                <th className="px-4 py-3">{i18n.t("admin.l.Published", "Published")}</th>
                                <th className="px-4 py-3">{i18n.t("admin.l.Updated", "Updated")}</th>
                                <th className="px-4 py-3 text-right">{i18n.t("admin.l.Actions", "Actions")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                            {docs.map((d) => (
                                <tr key={d.id}
                                    data-testid={`legal-doc-row-${d.document_type}-${d.version}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-neutral-900">
                                            {TYPE_LABEL[d.document_type] || d.document_type}
                                        </div>
                                        <div className="text-xs text-neutral-500 uppercase">
                                            {d.document_type}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 font-mono">{d.version}</td>
                                    <td className="px-4 py-3">
                                        <StatusBadge
                                            tone={d.is_active ? "success" : "neutral"}>
                                            {d.is_active ? i18n.t("admin.l.Active", "Active") : i18n.t("admin.l.Inactive", "Inactive")}
                                        </StatusBadge>
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge
                                            tone={d.is_required ? "warning" : "neutral"}>
                                            {d.is_required ? i18n.t("admin.l.Required", "Required") : i18n.t("admin.l.Informational", "Informational")}
                                        </StatusBadge>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-neutral-500">
                                        {formatDate(d.published_at)}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-neutral-500">
                                        {formatDate(d.updated_at)}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => setEditing(d)}
                                            className="text-neutral-500 hover:text-neutral-900"
                                            data-testid={`legal-edit-${d.document_type}-${d.version}`}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {creating && (
                <DocumentDialog
                    mode="create"
                    onClose={() => setCreating(false)}
                    onSaved={() => { setCreating(false); reload(); }}
                />
            )}
            {editing && (
                <DocumentDialog
                    mode="edit"
                    doc={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}


function DocumentDialog({ mode, doc, onClose, onSaved }) {
    const isEdit = mode === "edit";
    const [type, setType] = useState(doc?.document_type || "msa");
    const [version, setVersion] = useState(doc?.version || "");
    const [url, setUrl] = useState(doc?.url || "");
    const [isActive, setIsActive] = useState(doc?.is_active ?? true);
    const [isRequired, setIsRequired] = useState(doc?.is_required ?? false);
    const [materialChange, setMaterialChange] = useState(
        doc?.material_change ?? false);
    const [saving, setSaving] = useState(false);

    async function handleSave() {
        if (!version.trim() || !url.trim()) {
            toast.error(i18n.t("admin.l.Version_and_URL_are_required", "Version and URL are required"));
            return;
        }
        setSaving(true);
        try {
            const payload = {
                document_type: type,
                version: version.trim(),
                url: url.trim(),
                is_active: isActive,
                is_required: isRequired,
                material_change: materialChange,
            };
            if (isEdit) {
                await api.patch(`/admin/legal/documents/${doc.id}`, payload);
                toast.success(`${TYPE_LABEL[type] || type} v${version} updated`);
            } else {
                await api.post("/admin/legal/documents", payload);
                toast.success(`${TYPE_LABEL[type] || type} v${version} published`);
            }
            onSaved();
        } catch (err) {
            toast.error((i18n.language === "fi" ? apiErrorMessage(err) : formatApiErrorDetail(err)));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="legal-doc-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {isEdit
                            ? i18n.t("admin.editDoc", { defaultValue: "Edit {{name}}", name: TYPE_LABEL[type] || type })
                            : i18n.t("admin.l.Publish_New_Legal_Document", "Publish New Legal Document")}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.activeHint", "When Active is turned on, any previous active version of the same document type is automatically deactivated. History is preserved.")}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <div>
                        <label className="text-xs font-medium text-neutral-500 uppercase">
                            {i18n.t("admin.l.Document_Type", "Document Type")}
                        </label>
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            disabled={isEdit}
                            className="mt-1 w-full border border-neutral-300 rounded-md
                                       px-3 py-2 text-sm disabled:bg-neutral-50"
                            data-testid="legal-doc-type"
                        >
                            {DOC_TYPES.map((t) => (
                                <option key={t} value={t}>
                                    {TYPE_LABEL[t]} ({t})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-neutral-500 uppercase">
                            {i18n.t("admin.l.Version", "Version")}
                        </label>
                        <input
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            placeholder="1.0.0"
                            className="mt-1 w-full border border-neutral-300 rounded-md
                                       px-3 py-2 text-sm font-mono"
                            data-testid="legal-doc-version"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-neutral-500 uppercase">
                            URL
                        </label>
                        <input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://miseone.eu/..."
                            className="mt-1 w-full border border-neutral-300 rounded-md
                                       px-3 py-2 text-sm"
                            data-testid="legal-doc-url"
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox"
                                   checked={isActive}
                                   onChange={(e) => setIsActive(e.target.checked)}
                                   data-testid="legal-doc-active" />
                            {i18n.t("admin.l.Active", "Active")}
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox"
                                   checked={isRequired}
                                   onChange={(e) => setIsRequired(e.target.checked)}
                                   data-testid="legal-doc-required" />
                            {i18n.t("admin.l.Required", "Required")}
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox"
                                   checked={materialChange}
                                   onChange={(e) => setMaterialChange(e.target.checked)}
                                   data-testid="legal-doc-material" />
                            {i18n.t("admin.l.Material_change", "Material change")}
                        </label>
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        {i18n.t("admin.l.Cancel", "Cancel")}
                    </Button>
                    <Button onClick={handleSave} disabled={saving}
                            data-testid="legal-doc-save">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isEdit ? i18n.t("admin.l.Save_changes", "Save changes") : i18n.t("admin.l.Publish", "Publish")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function CompanyStatusPanel() {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/legal/company-status");
            setState(data);
        } catch (err) {
            toast.error((i18n.language === "fi" ? apiErrorMessage(err) : formatApiErrorDetail(err)));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    if (loading && state === null) {
        return <div className="py-10 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" />
        </div>;
    }
    if (state === null) return null;

    const activeTypes = state.active_documents.map((d) => d.document_type);

    return (
        <div className="space-y-4" data-testid="legal-company-status-panel">
            <div className="text-sm text-neutral-500">
                {state.companies.length} compan{state.companies.length === 1 ? "y" : "ies"} ·
                {" "}{activeTypes.length} active document{activeTypes.length === 1 ? "" : "s"}
            </div>
            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-left text-xs
                                      uppercase tracking-wider text-neutral-500">
                        <tr>
                            <th className="px-4 py-3">{i18n.t("admin.l.Company", "Company")}</th>
                            {activeTypes.map((t) => (
                                <th key={t} className="px-3 py-3 whitespace-nowrap">
                                    {t.toUpperCase()}
                                </th>
                            ))}
                            <th className="px-4 py-3">{i18n.t("admin.l.Pending", "Pending")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.Last_accepted", "Last accepted")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {state.companies.map((c) => (
                            <tr key={c.company_id}
                                data-testid={`legal-company-row-${c.company_id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-medium">{c.company_name}</div>
                                    <div className="text-xs text-neutral-500">
                                        {c.lifecycle_status || "—"}
                                        {c.legacy_customer ? i18n.t("admin.legacyShort", " · legacy") : ""}
                                    </div>
                                </td>
                                {activeTypes.map((t) => {
                                    const cur = (c.legal_current || {})[t];
                                    return (
                                        <td key={t} className="px-3 py-3 whitespace-nowrap
                                                                text-xs font-mono">
                                            {cur?.version || "—"}
                                        </td>
                                    );
                                })}
                                <td className="px-4 py-3">
                                    {c.pending_required.length === 0 ? (
                                        <StatusBadge tone="success">{i18n.t("admin.l.Current", "Current")}</StatusBadge>
                                    ) : (
                                        <span className="text-xs text-amber-700">
                                            {c.pending_required.map(
                                                (t) => t.toUpperCase()).join(", ")}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-xs text-neutral-500">
                                    {formatDate(c.last_accepted_at)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
